import React, { useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import { push } from 'react-router-redux';
import { Button, ButtonToolbar, Col, Row } from 'reactstrap';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import { getItineraryForCounter } from '../../../../actions';
import {
  generateITineraryIncidentPath,
  ITINERARY_INCIDENT_PATH,
} from '../../../../config/paths';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import Content from '../../../layout/Content';
import IsActive from '../../../common/cells/IsActive';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import { getItineraryIncidents } from '../../../../actions/supervision-control';
import Table from '../../../common/Table';
import { ITINERARY_INCIDENT_COLUMNS } from '../../../../config/columns';
import { DEFAULT_PAGE_SIZE } from '../../../../config/constants';

const ItineraryIncident = ({
  match: {
    params: { itineraryId, segmentId },
  },
  itinerary,
  dispatchGetItineraryForCounter,
  loading,
  breadcrumbs,
  dispatchPush,
  dispatchGetItineraryIncidents,
  itineraryIncidents,
  itineraryIncidentsLoading,
}) => {
  useLayoutEffect(() => {
    dispatchGetItineraryForCounter(itineraryId);
    dispatchGetItineraryIncidents({ itineraryId, segmentId });
  }, []);

  const actualSegment = (actualItinerary, actualSegmentId) => {
    const { itineraryScheduleSegmentList } = actualItinerary;

    if (!itineraryScheduleSegmentList || !itineraryScheduleSegmentList.length) {
      return null;
    }

    const itineraryScheduleSegment = itineraryScheduleSegmentList.find(
      (iss) => iss.segmentId === Number(actualSegmentId),
    );

    if (!itineraryScheduleSegment || !itineraryScheduleSegment.segment) {
      return null;
    }

    return itineraryScheduleSegment.segment;
  };

  let content;

  if (loading || itineraryIncidentsLoading) {
    content = <Loader />;
  } else if (itinerary.isEmpty()) {
    content = <NoDataResource returnPage={ITINERARY_INCIDENT_PATH} />;
  } else {
    const itineraryBasicResource = (
      <>
        <h3>Información Básica</h3>
        <ResourceProperty label="Id:">{itinerary.get('id')}</ResourceProperty>
        <ResourceProperty label="Bus">
          {itinerary.get('companyBusId')}
        </ResourceProperty>
        <ResourceProperty label="Tipo de Servicio:">
          {itinerary.get('serviceTypeName')}
        </ResourceProperty>
        <ResourceProperty label="Estado:">
          <IsActive value={itinerary.get('active')} />
        </ResourceProperty>
      </>
    );

    const segment = actualSegment(itinerary.toJS(), segmentId);

    if (!segment || !segment.sourceLocation || !segment.destinationLocation) {
      content = <Loader />;
    } else {
      const { destinationLocation, sourceLocation } = segment;

      content = (
        <>
          {itineraryBasicResource}
          <h3>Conductores</h3>
          {itinerary.get('driverList').map((driver, index) => (
            <ResourceProperty key={driver.id} label={`Conductor ${index + 1}:`}>
              {driver.fullName} - Doc. {driver.idDocumentNumber}
            </ResourceProperty>
          ))}
          <h3>Segmento</h3>
          <Row>
            <Col md={6}>
              <h5>Origen</h5>
              <ResourceProperty label="Nombre:">
                {sourceLocation.name}
              </ResourceProperty>
              <ResourceProperty label="Descripción:">
                {sourceLocation.description}
              </ResourceProperty>
              <ResourceProperty label="Dirección:">
                {sourceLocation.address.address}
              </ResourceProperty>
              <ResourceProperty label="Distrito:">
                {sourceLocation.address.district}
              </ResourceProperty>
            </Col>
            <Col md={6}>
              <h5>Destino</h5>
              <ResourceProperty label="Nombre:">
                {destinationLocation.name}
              </ResourceProperty>
              <ResourceProperty label="Descripción:">
                {destinationLocation.description}
              </ResourceProperty>
              <ResourceProperty label="Dirección:">
                {destinationLocation.address.address}
              </ResourceProperty>
              <ResourceProperty label="Distrito:">
                {destinationLocation.address.district}
              </ResourceProperty>
            </Col>
          </Row>
          <h3>Incidentes</h3>
          <Table
            data={itineraryIncidents}
            columns={ITINERARY_INCIDENT_COLUMNS}
            manual={false}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            filterable
            navigateToModelOnRowClick
            isExternalPath
            modelPath={`${generateITineraryIncidentPath({
              itineraryId,
              segmentId,
            })}/incident-record`}
          />
        </>
      );
    }
  }

  const toolbar = (
    <ButtonToolbar className="pull-right">
      <div>
        <Button
          className="pull-right"
          type="button"
          color="primary"
          disabled={loading}
          onClick={() =>
            dispatchPush(
              `${generateITineraryIncidentPath({
                itineraryId,
                segmentId,
              })}/new`,
            )
          }
        >
          <i className="fa fa-plus-circle" /> Crear
        </Button>
      </div>
    </ButtonToolbar>
  );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Incidente en Itinerario"
      content={content}
      toolbar={toolbar}
    />
  );
};

ItineraryIncident.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  match: matchPropTypes.isRequired,
  itinerary: PropTypes.instanceOf(Immutable.Map).isRequired,
  dispatchGetItineraryForCounter: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  dispatchPush: PropTypes.func.isRequired,
  dispatchGetItineraryIncidents: PropTypes.func.isRequired,
  itineraryIncidents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      incidentId: PropTypes.number,
      incidentSubtypeId: PropTypes.number,
      segmentId: PropTypes.number,
      itineraryId: PropTypes.number,
      agencyId: PropTypes.number,
      incidentRecordType: PropTypes.string,
      userId: PropTypes.number,
      note: PropTypes.string,
      createDate: PropTypes.number,
      firstSegment: PropTypes.bool,
    }),
  ).isRequired,
  itineraryIncidentsLoading: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ ItineraryUnit, SupervisionControlUnit }) => ({
  breadcrumbs: [
    ...SupervisionControlUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Incidentes en Itinerarios',
      href: ITINERARY_INCIDENT_PATH,
    },
    {
      text: 'Incidente en Itinerario',
      href: '',
    },
  ],
  itinerary: ItineraryUnit.Itinerary.getIn(['current', 'content']),
  loading: !ItineraryUnit.Itinerary.getIn(['current', 'activity']).isEmpty(),
  itineraryIncidents: SupervisionControlUnit.ItineraryIncident.getIn([
    'itineraryIncident',
    'content',
    'content',
  ]),
  itineraryIncidentsLoading: SupervisionControlUnit.ItineraryIncident.getIn([
    'itineraryIncident',
    'loading',
  ]),
});

const mapDispatchToProps = {
  dispatchGetItineraryForCounter: getItineraryForCounter,
  dispatchPush: push,
  dispatchGetItineraryIncidents: getItineraryIncidents,
};

export default connect(mapStateToProps, mapDispatchToProps)(ItineraryIncident);
